import React, { useState } from 'react';
import { Link, useNavigate } from "react-router-dom";
import {
  faBed,
  faShareAlt,
  faRulerCombined,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { Rotate3d } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useTokenStore from "../../store/store";
import useLocaleStore from "../../store/localeStore";
import { numberFormat } from "../../utils/properties";
import useThemeStore from "../../store/themeStore";
import { ThemeProps, getCurrencySymbol } from "../../utils/theme";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { doesThemeSkipLogin } from '../../store/themesWithoutLogin';
import { toggleFavoriteProperty } from '../../utils/favourites';
import { useFavoritesStore } from '../../store/favoritesStore';
import { t } from "../../i18n/translations";

interface PropertyCardProps {
  id: string;
  discount: string;
  image: string;
  streetName: string;
  price: string;
  bedrooms: string;
  value: string;
  rent: string;
  netCashFlow: string;
  grossYield: string;
  strategy?: string;
  discountLabel?: string;
  available: boolean;
  tenureType?: string;
  occupancy?: string;
  floorsqft?: string;
  region?: string;
  investment?: string;
  isFeatured?: boolean;
  asteroomUrl?: string;
  onShareClick: () => void;
  onClick: () => void;
  onAsteroomClick?: () => void;
}

const PropertyCard = ({
  id,
  discount,
  available,
  image,
  streetName,
  price,
  bedrooms,
  value,
  rent,
  grossYield,
  netCashFlow,
  strategy,
  occupancy,
  tenureType,
  floorsqft,
  region,
  investment,
  asteroomUrl,
  onShareClick,
  isFeatured = false,
  onClick,
  onAsteroomClick,
}: PropertyCardProps) => {
  const getToken = useTokenStore((state) => state.token);
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const navigate = useNavigate();
  useLocaleStore((state) => state.locale);
  const [heartPulse, setHeartPulse] = useState(false);
  const { favoriteIds, fetchFavorites } = useFavoritesStore();
  const isFavorite = favoriteIds.includes(id);

  const handleClick = (e: React.MouseEvent) => {
    const themeSkipsLogin = doesThemeSkipLogin(getThemes);
    if (themeSkipsLogin) return;

    if (!getToken) {
      sessionStorage.setItem('intendedPropertyId', id);
      e.preventDefault();
      navigate("/login");
      return;
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!getToken) {
      sessionStorage.setItem('intendedPropertyId', id);
      navigate("/login");
      return;
    }
    
    try {
      // Visual feedback before API response
      setHeartPulse(true);
      setTimeout(() => setHeartPulse(false), 800);
      
      const response = await toggleFavoriteProperty(id, getToken);
      if (response.status === 200) {
        fetchFavorites(); // Refresh favorites list
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const roundedYield = Math.ceil(Number(grossYield));
  const roundedInvestment = Math.round(Number(investment));

  const displayStrategy = getThemes?.useTenureForFilter
    ? (tenureType ?? strategy ?? "")
    : strategy === "Housing Association"
      ? "H.A"
      : strategy === "Serviced Accommodation"
        ? "Serviced"
        : strategy;

  const getBackgroundAndTextColor = (color: string, fallbackColor: string, defaultTextColor: string) => {
    if (isWhiteOrShade(color)) {
      return { backgroundColor: fallbackColor, textColor: 'white' };
    } else {
      return { backgroundColor: color, textColor: defaultTextColor };
    }
  };

  const isWhiteOrShade = (color: string) => {
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const themesWithoutZoopla = ['properties.mypropertylisting.co.uk', 'Test Site 1', 'Test Site 2'];
  const valuationLabel = getThemes?.valuationLabel
    ?? (themesWithoutZoopla.includes(getThemes?.name) ? t("card.netMonthlyCashflow") : t("card.zooplaValuation"));
  const valuationValue = getThemes?.valuationSameAsPrice
    ? (price as unknown as number)
    : valuationLabel === t("card.netMonthlyCashflow")
      ? Math.ceil(Number(netCashFlow))
      : (value as unknown as number);
  const currencySym = getCurrencySymbol(getThemes);

  return (
    <Link to={`/listing/${id}`} onClick={handleClick}>
      <div className="cursor-pointer relative">
        <div className={`w-full mb-5 ${isFeatured ? 'ring-2 ring-yellow-400' : ''}`}>
          <div className="border rounded-xl rounded- relative shadow-md hover:shadow-xl transition duration-150 ease-in-out">
            
            {/* Top Bar with Strategy and Yield */}
            <div className="flex justify-between items-center p-2 bg-gray-100 rounded-t-xl"
              style={{
                background: `${getThemes?.color1}`,
                color: `${getThemes?.txtColor}`,
              }}>
              <span className="font-bold">
                {displayStrategy}
              </span>
              <span className="font-bold">
                {roundedYield}% {strategy === "Housing Association" ? t("card.netYield") : t("card.grossYield")}
              </span>
            </div>

            {/* Property Image */}
            <div className="relative">
              <LazyLoadImage
                src={image}
                width={150}
                height={150}
                className="h-[250px] w-full object-cover"
                alt={t("card.propertyImage")}
              />
              
              {/* Favorite Heart - Enhanced version */}
<button 
  className={`absolute top-2 right-2 shadow-lg transition-all duration-300 
  ${heartPulse ? 'animate-pulse scale-110' : ''} 
  ${isFavorite ? 'cursor-wait' : 'hover:scale-125 hover:shadow-xl active:scale-90'}`}
  onClick={handleToggleFavorite}
  style={{
    background: isFavorite 
      ? `${isWhiteOrShade(getThemes?.color1) ? '#000000' : getThemes?.color1 || '#ffffff'}`
      : '#ffffff',
    padding: '10px',
    borderRadius: '50%',
    aspectRatio: '1/1',
    width: '42px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: isFavorite 
      ? '0 4px 10px rgba(0, 0, 0, 0.25), 0 0 15px rgba(0, 0, 0, 0.2)' 
      : '0 4px 8px rgba(0, 0, 0, 0.15)',
    cursor: 'pointer',
  }}
>
  <FontAwesomeIcon 
    icon={faHeart} 
    className="text-xl transition-all duration-300"
    style={{
      color: isFavorite 
        ? '#ffffff' // White heart on theme color background
        : '#000000', // Black when not favorited
      filter: isFavorite ? 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))' : 'none',
    }}
  />
</button>

              {/* Asteroom Icon - only show if asteroomUrl exists */}
              {asteroomUrl && onAsteroomClick && (
                <button 
                  className="absolute top-16 right-2 shadow-lg transition-all duration-300 hover:scale-125 hover:shadow-xl active:scale-90"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onAsteroomClick();
                  }}
                  style={{
                    background: '#ffffff',
                    padding: '10px',
                    borderRadius: '50%',
                    aspectRatio: '1/1',
                    width: '42px',
                    height: '42px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                    cursor: 'pointer',
                  }}
                >
                  <Rotate3d 
                    className="text-xl transition-all duration-300"
                    style={{
                      color: '#000000',
                    }}
                  />
                </button>
              )}
              
            </div>

            {/* Property Details */}
            <div className="p-4 bg-white-f2f9fa">
              <h3 className="font-roboto font-medium text-lg mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                {streetName}
              </h3>
              <p className="text-gray-600 mb-2 whitespace-nowrap overflow-hidden text-ellipsis">
                {region && region !== "false" ? region : <span>&nbsp;</span>}
              </p>
              
              <div className="flex flex-wrap gap-2 mb-2 overflow-hidden">
                {!available && (
                  <span
                    className="text-xs font-bold py-1 px-2 rounded-md"
                    style={{
                      background: isWhiteOrShade(getThemes?.color1) ? 'black' : getThemes?.color2,
                      color: isWhiteOrShade(getThemes?.color1) ? "white" : getThemes?.txtColor,
                    }}>
                    {t("card.reserved")}
                  </span>
                )}
                {parseFloat(discount) > 0 && (
                  <span 
                    className="text-xs font-bold text-white py-1 px-2 rounded-md"
                    style={{
                      background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                      color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                    }}
                  >
                    {discount}
                  </span>
                )}
                {occupancy?.toLowerCase() === 'tenanted' && (
                  <span className="text-xs font-bold text-white py-1 px-2 rounded-md"
                    style={{
                      background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                      color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                    }}>
                    {t("card.tenanted")}
                  </span>
                )}
                {tenureType && (
                  <span className="text-xs font-bold text-white py-1 px-2 rounded-md"
                    style={{
                      background: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).backgroundColor,
                      color: getBackgroundAndTextColor(getThemes.color1, getThemes.color2, getThemes.txtColor).textColor,
                    }}>
                    {tenureType}
                  </span>
                )}
                {asteroomUrl && (
                  <span 
                    className="text-xs font-bold text-white py-1 px-2 rounded-md cursor-pointer hover:opacity-80 transition-opacity duration-200 flex items-center gap-1"
                    style={{
                      background: getThemes?.color2,
                      color: '#f2f9fa',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onAsteroomClick && onAsteroomClick();
                    }}
                  >
                    <Rotate3d className="w-3 h-3" />
                    {t("card.threeDTourAvailable")}
                  </span>
                )}
              </div>

              {/* Info List */}
              <ul className="mt-3">
                <li className="flex justify-between py-1">
                  <span className="font-roboto font-medium">{t("card.askingPrice")}</span>
                  <span className="font-bold text-green-700">{currencySym}{numberFormat(price as unknown as number)}</span>
                </li>
                <li className="flex justify-between py-1">
                  <span>{valuationLabel}</span>
                  <span className="font-bold">
                    {currencySym}{numberFormat(valuationValue)}
                  </span>
                </li>
                <li className="flex justify-between py-1">
                  <span>{t("card.totalInvestment")}</span>
                  <span className="font-bold">{currencySym}{numberFormat(roundedInvestment)}</span>
                </li>
                <li className="flex justify-between py-1">
                  <span>{t("card.rentalIncomePcm")}</span>
                  <span className="font-bold">{currencySym}{numberFormat(rent as unknown as number)}</span>
                </li>
              </ul>

              {/* Icons and Share */}
              <div className="flex justify-between items-center mt-3">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faBed} />
                  <span>{bedrooms}</span>
                </div>
                {floorsqft && (
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faRulerCombined} />
                    <span>{floorsqft}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onShareClick();
                    }} 
                    className="focus:outline-none"
                    style={{ transition: 'transform 0.2s', fontSize: '1.1rem' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                    <FontAwesomeIcon icon={faShareAlt} />
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;